import { useCallback, useState } from 'react';
import { useSignInWithOtp } from '@/contexts/auth';
import { AuthModalState } from '@/types/auth-types';
import type { AuthModalStateTarget } from './authModalStateTypes';

const EMAIL_REQUIRED_ERROR = 'Email Required';
const EMAIL_INVALID_ERROR = 'Invalid Email';
const AUTH_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface EmailAuthHandlers {
  email: string;
  emailInputError: string;
  loadingEmail: boolean;
  handleEmailAuth: () => void;
  handleEmailChange: (value: string) => void;
}

export function useEmailAuthHandlers({
  setError,
  setModalState,
}: AuthModalStateTarget): EmailAuthHandlers {
  const signInWithOtp = useSignInWithOtp();
  const [email, setEmail] = useState('');
  const [emailInputError, setEmailInputError] = useState<string>('');
  const [loadingEmail, setLoadingEmail] = useState(false);

  const handleEmailAuth = useCallback((): void => {
    if (!email.trim()) {
      setEmailInputError(EMAIL_REQUIRED_ERROR);
      return;
    }

    if (!AUTH_EMAIL_PATTERN.test(email)) {
      setEmailInputError(EMAIL_INVALID_ERROR);
      return;
    }

    setLoadingEmail(true);
    setEmailInputError('');
    setError(null);

    signInWithOtp.invoke(
      { email },
      {
        onSuccess: () => {
          setModalState(AuthModalState.EmailSent);
        },
        onError: (authError) => {
          setModalState(AuthModalState.Error);
          setError({
            title: 'Authentication Failed',
            message: authError instanceof Error
              ? authError.message
              : 'Unable to send magic link. Please try again.',
          });
        },
        onSettled: () => {
          setLoadingEmail(false);
        },
      },
    );
  }, [email, setError, setModalState, signInWithOtp]);

  const handleEmailChange = useCallback((value: string): void => {
    setEmail(value);
    setEmailInputError('');
  }, []);

  return {
    email,
    emailInputError,
    loadingEmail,
    handleEmailAuth,
    handleEmailChange,
  };
}
